import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { debounce } from "lodash";
import { LinearProgress } from "@mui/material";
import { useDispatch } from "react-redux";

import Sidebar from "../Components/Sidebars/main";
import NavBar from "../Components/Navbars/main";
import ActionBarComponent from "../Components/common/ActionBarComponent";
import NotesSectionDashboard from "../Components/NotesSectionDashboard/main";
import Footer from "../Components/common/footer";
import SimpleIcon from "../assets/images/simple-icon.svg";
import DetailIcon from "../assets/images/detail.svg";
import DatesIcon from "../assets/images/dates.svg";
import useGetPhotoTabData from "../Components/Photo/new-photo-work/hooks/useGetTabs";
import usePhotoSearchData from "../Components/Photo/new-photo-work/hooks/useSearchData";
import ResponsiveTabPhotoContainer from "../Components/Photo/new-photo-work/hooks/responsiveTabPhotoContainer";
import NewPhotoTab from "../Components/Photo/NewPhotoPage/new-photo-tab";
import NewPhotoDetailedTab from "../Components/Photo/NewPhotoPage/new-photo-detail-tab";
import NewPhotoShownOnPage from "../Components/Photo/NewPhotoPage/new-photo-shown-on-page";
import CaseUploadButton from "../Components/common/CaseUploadButton";
import { fetchPageObject, fetchShakespeareStatus, getCaseId, getClientId } from "../Utils/helper";
import NewPhotoSummaryTab from "../Components/Photo/NewPhotoPage/new-photo-summary-tab";
import { api_without_cancellation } from "../api/api";

const NewPhotoPage = () => {
  const [activeTab, setActiveTab] = useState("all");
  const [_activeTabName, setActiveTabName] = useState("All");
  const [currentPage, setCurrentPage] = useState("summary");
  const dispatch = useDispatch();
  const caseId = getCaseId();
  const [pageObject, setPageObject] = useState(null);

  useEffect(() => {
    if (caseId && !pageObject) {
      fetchPageObject(caseId, 6).then((data) => {
        setPageObject(data);
      });
    }
  }, [caseId, pageObject]);

  const handlePageChange = useCallback((page) => {
    setCurrentPage(page);
  }, []);

  const { pageSlots, unsortedCount, allCount, refetch } = useGetPhotoTabData();

  const { tabData, loading: searchLoading, refetch: fetchSearch } = usePhotoSearchData(activeTab);

  const [searchTerm, setSearchTerm] = useState("");
  const [query, setQuery] = useState("");

  useEffect(() => {
    fetchShakespeareStatus(getCaseId(), 6, dispatch);
  }, [dispatch]);

  const [_loadingNew, setLoadingNew] = useState(false);
  const [simpleDetailData, setSimpleDetailData] = useState([]);
  const PGSIZE = 25;
  const [pageNo, setPageNo] = useState(1);

  const fetchDataforRefetch = useCallback(
    async ({ query }) => {
      try {
        setLoadingNew(true);
        const res = await api_without_cancellation.get("/api/photo/list-photos-centric/", {
          params: {
            case_id: getCaseId(),
            client_id: getClientId(),
            page_id: activeTab === "all" ? "" : activeTab,
            page: pageNo,
            page_size: PGSIZE,
            query,
          },
        });
        setSimpleDetailData(res.data);
      } catch (error) {
        console.error(error);
      } finally {
        setLoadingNew(false);
      }
    },
    [activeTab, pageNo]
  );

  const tabsData = [
    {
      id: "summary",
      label: "Summary",
      className: "tab-trapezium-cycles-corners",
      background: "var(--primary-60)",
      onClick: () => handlePageChange("summary"),
      leftHand: true,
      span: (
        <span
          style={{
            width: "15px",
            height: "15px",
          }}
          className="fw-bold d-flex align-items-center justify-content-center  text-gold"
        >
          <img
            src={DatesIcon}
            style={{
              width: "15px",
              height: "15px",
            }}
            alt="Detail"
          />
        </span>
      ),
    },
    {
      id: "simple",
      label: "Simple",
      className: "tab-trapezium-cycles-corners",
      background: "var(--primary-60)",

      onClick: () => handlePageChange("simple"),
      leftHand: true,
      span: (
        <span
          style={{
            width: "15px",
            height: "15px",
          }}
          className="fw-bold d-flex align-items-center justify-content-center  text-gold"
        >
          <img
            src={SimpleIcon}
            style={{
              width: "15px",
              height: "15px",
            }}
            alt="Detail"
          />
        </span>
      ),
    },
    {
      id: "detail",
      label: "Detailed",
      className: "tab-trapezium-cycles-corners",
      background: "var(--primary-60)",

      onClick: () => handlePageChange("detail"),
      leftHand: true,
      span: (
        <span
          style={{
            width: "15px",
            height: "15px",
          }}
          className="fw-bold d-flex align-items-center justify-content-center  text-gold"
        >
          <img
            src={DetailIcon}
            style={{
              width: "15px",
              height: "15px",
            }}
            alt="Detail"
          />
        </span>
      ),
    },
    {
      id: "default",
      label: "Rows",
      className: "tab-trapezium-cycles-corners",
      background: "var(--primary-60)",
      left: "0px",
      onClick: () => handlePageChange("default"),
      leftHand: true,
      span: (
        <span
          style={{
            width: "15px",
            height: "15px",
          }}
          className="fw-bold d-flex align-items-center justify-content-center  text-gold"
        >
          <img
            src={DetailIcon}
            style={{
              width: "15px",
              height: "15px",
            }}
            alt="Detail"
          />
        </span>
      ),
    },
  ];

  const unsortedTab = [
    {
      id: "unsorted",
      label: "Unsorted",
      className: "tab-trapezium-cycles-corners",
      background: "var(--primary-5)",
      onClick: () => setActiveTab("unsorted"),
    },
  ];

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoadingNew(true);
        const res = await api_without_cancellation.get("/api/photo/list-photos-centric/", {
          params: {
            case_id: getCaseId(),
            client_id: getClientId(),
            page_id: activeTab === "all" ? "" : activeTab,
            page: pageNo,
            page_size: PGSIZE,
            query,
          },
        });
        setSimpleDetailData(res.data);
      } catch (error) {
        console.error(error);
      } finally {
        setLoadingNew(false);
      }
    };

    fetchData();
  }, [pageNo, activeTab, pageNo]);

  const handleSelect = async (selectedTab, selectedTabName) => {
    setActiveTab(selectedTab);
    setActiveTabName(selectedTabName);
    // fetchSearch({
    //   query: query,
    //   page_id: selectedTab === "all" ? "" : selectedTab,
    //   all_docs: selectedTab === "all" ? "True" : false,
    // });
    // fetchDataforRefetch({
    //   query: query,
    // });
  };

  const [sideBarCollapsed, setSideBarCollapsed] = useState(null);

  useEffect(() => {
    const getUserPreferencesFromStorage = () => {
      const preferences = localStorage.getItem("userPreferences");
      const preferencesObj = preferences ? JSON.parse(preferences) : null;
      setSideBarCollapsed(preferencesObj?.SidebarView);
    };
    getUserPreferencesFromStorage();
  }, []);
  const containerHeight = useRef(null);
  //
  const [rowsTabData, setRowsTabData] = useState(null);
  const refetchRowsData = useCallback(
    async ({ query }) => {
      try {
        const res = await api_without_cancellation.get("/api/photo/photos-page-rows/", {
          params: {
            case_id: getCaseId(),
            client_id: getClientId(),
            page_id: activeTab === "all" ? "" : activeTab,
            query,
          },
        });
        setRowsTabData(res.data);
      } catch (error) {
        console.error(error);
      }
    },
    [activeTab]
  );

  useEffect(() => {
    const fetchData = async () => {
      try {
        const res = await api_without_cancellation.get("/api/photo/photos-page-rows/", {
          params: {
            case_id: getCaseId(),
            client_id: getClientId(),
            page_id: activeTab === "all" ? "" : activeTab,
          },
        });
        setRowsTabData(res.data);
      } catch (error) {
        console.error(error);
      }
    };

    fetchData();
  }, [pageNo, activeTab]);

  const refreshPhotoData = useCallback(
    () =>
      Promise.all([
        fetchDataforRefetch({ query }),
        refetchRowsData({ query }),
        fetchSearch({ query }),
        refetch({ query }),
      ]),
    [fetchDataforRefetch, refetchRowsData, fetchSearch, refetch, query]
  );

  const debouncedSearch = useMemo(
    () =>
      debounce((value) => {
        fetchDataforRefetch({ query: value });
        fetchSearch({ query: value });
        refetchRowsData({ query: value });
        refetch({ query: value });
      }, 500),
    [fetchSearch, fetchDataforRefetch, refetchRowsData, refetch]
  );

  const handleInputChange = useCallback(
    (event) => {
      const value = event.target.value;
      setSearchTerm(value);
      setQuery(value);
      debouncedSearch(value);
    },
    [debouncedSearch]
  );

  React.useEffect(() => () => debouncedSearch.cancel(), [debouncedSearch]);

  return (
    <>
      <div className={`page-wrapper ${sideBarCollapsed ? "force-collapsed" : ""}`}>
        <Sidebar />
        <div className="page-container overflow-hidden">
          <NavBar flaggedPageName={"6"} />
          <ActionBarComponent
            src={pageObject?.page_icon}
            page_name={pageObject?.name}
            pageObj={pageObject}
            isChecklist={pageObject?.PageChklst || false}
          />
          <div className="TreatmentPage-main-container invisible-scrollbar" ref={containerHeight}>
            <div className="d-flex align-items-center m-b-5 p-r-5 w-100 height-25 ps-0">
              <textarea
                className=" height-25 m-0 note_assign_text note_assign_text-new"
                // className="note_assign_text slanted-textarea"
                style={{
                  padding: "0px 10px",
                  resize: "none",
                  width: "100%",
                  height: "25px",
                  border: "2px solid var(--primary-25)",
                }}
                placeholder="Type three or more characters to search documents on the case..."
                value={searchTerm}
                onChange={handleInputChange}
              />
            </div>

            <ResponsiveTabPhotoContainer
              onSelect={handleSelect}
              data={pageSlots}
              allCount={allCount}
              unsortedCount={unsortedCount}
              activeTab={activeTab}
              tabsData={tabsData}
              photoTab={unsortedTab}
              currentPage={currentPage}
            />
            <div
              style={{
                width: "100%",
                justifyContent: "flex-start",
                background: "var(--primary)",
                height: "25px",
                marginBottom: "5px",
                position: "relative",
              }}
            >
              <span
                className="fw-bold text-white"
                style={{
                  height: "25px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  textTransform: "capitalize",
                }}
              >
                {currentPage === "default" ? "Shown on Page" : currentPage} View
              </span>
              <div
                className="d-flex align-items-center"
                style={{ position: "absolute", top: 0, right: 9, height: 25, gap: 10 }}
              >
                {Boolean(simpleDetailData?.total_pages) &&
                  ["simple", "detail", "summary"].includes(currentPage) && (
                    <div
                      className=""
                      style={{
                        height: "25px",
                      }}
                    >
                      <div
                        className="d-flex align-items-center"
                        style={{
                          color: "",
                        }}
                      >
                        <span
                          className=""
                          style={{
                            color: "var(--primary-25)",
                            fontWeight: "600",
                            textTransform: "uppercase",
                            // height: "25px",
                          }}
                        >
                          PAGE:
                        </span>
                        {(() => {
                          const totalPages = simpleDetailData.total_pages;

                          const current = pageNo;
                          let pages = [];

                          if (totalPages <= 22) {
                            pages = Array.from({ length: totalPages }, (_, i) => i + 1);
                          } else {
                            if (current <= 7) {
                              for (let i = 1; i <= 8; i++) {
                                pages.push(i);
                              }
                              pages.push("...");
                              pages.push(totalPages);
                            } else if (current >= totalPages - 3) {
                              pages.push(1);
                              pages.push("...");
                              for (let i = totalPages - 4; i <= totalPages; i++) {
                                pages.push(i);
                              }
                            } else {
                              pages.push(1);

                              if (current - 1 > 2) {
                                pages.push("...");
                              }

                              for (let i = current - 1; i <= current + 1; i++) {
                                pages.push(i);
                              }

                              if (current + 1 < totalPages - 1) {
                                pages.push("...");
                              }

                              pages.push(totalPages);
                            }
                          }

                          return pages.map((page, idx) => {
                            if (page === "...") {
                              return (
                                <span
                                  key={`ellipsis-${idx}`}
                                  className="px-2"
                                  style={{ color: "white" }}
                                >
                                  ...
                                </span>
                              );
                            }

                            return (
                              <button
                                key={page}
                                style={{
                                  color: page === pageNo ? "white" : "var(--primary-25)",
                                  fontWeight: "600",
                                  height: "25px",

                                  width: "20px",
                                }}
                                className="bg-hover-page-nos p-0"
                                onClick={() => setPageNo(page)}
                              >
                                {page}
                              </button>
                            );
                          });
                        })()}
                      </div>
                    </div>
                  )}
                <CaseUploadButton onUploaded={refreshPhotoData} />
              </div>
            </div>

            {searchLoading ? (
              <>
                <div
                  style={{
                    width: "100%",
                    padding: "5px",
                    margin: "25px auto",
                    color: "var(--primary)",
                  }}
                  className="m-t-25"
                >
                  <LinearProgress key={searchLoading} color="inherit" />
                </div>
                <div
                  style={{
                    width: "100%",
                    padding: "5px",
                    margin: "25px auto",
                    color: "var(--primary)",
                  }}
                  className="m-t-25"
                >
                  <LinearProgress color="inherit" />
                </div>
                <div
                  style={{
                    width: "100%",
                    padding: "5px",
                    margin: "25px auto",
                    color: "var(--primary)",
                  }}
                  className="m-t-25"
                >
                  <LinearProgress color="inherit" />
                </div>
                <div
                  style={{
                    width: "100%",
                    padding: "5px",
                    margin: "25px auto",
                    color: "var(--primary)",
                  }}
                  className="m-t-25"
                >
                  <LinearProgress color="inherit" />
                </div>
                <div
                  style={{
                    width: "100%",
                    padding: "5px",
                    margin: "25px auto",
                    color: "var(--primary)",
                  }}
                  className="m-t-25"
                >
                  <LinearProgress color="inherit" />
                </div>
                <div
                  style={{
                    width: "100%",
                    padding: "5px",
                    margin: "25px auto",
                    color: "var(--primary)",
                  }}
                  className="m-t-25"
                >
                  <LinearProgress color="inherit" />
                </div>
                <div
                  style={{
                    width: "100%",
                    padding: "5px",
                    margin: "25px auto",
                    color: "var(--primary)",
                  }}
                  className="m-t-25"
                >
                  <LinearProgress color="inherit" />
                </div>
                <div
                  style={{
                    width: "100%",
                    padding: "5px",
                    margin: "25px auto",
                    color: "var(--primary)",
                  }}
                  className="m-t-25"
                >
                  <LinearProgress color="inherit" />
                </div>
              </>
            ) : currentPage === "simple" ? (
              <>
                <NewPhotoTab
                  // data={
                  //   activeTab === "all"
                  //     ? tabData?.data || []
                  //     : // eslint-disable-next-line no-constant-binary-expression
                  //       [
                  //         {
                  //           page_docs: tabData?.data,
                  //           panels: tabData?.panels,
                  //           page: tabData?.page,
                  //         },
                  //       ] || []
                  // }
                  // data={tabData?.data || []}
                  data={simpleDetailData}
                  loading={searchLoading}
                  refetchData={refreshPhotoData}
                  refetchLoading={searchLoading}
                  pageView={currentPage}
                  // tabData={
                  //   activeTab === "all"
                  //     ? tabData?.data || []
                  //     : [
                  //         {
                  //           page_docs: tabData?.data,
                  //           panels: tabData?.panels,
                  //           page: tabData?.page,
                  //         },
                  //       ]
                  // }
                  // activeTab={activeTab}
                />
              </>
            ) : currentPage === "detail" ? (
              <>
                <NewPhotoDetailedTab
                  // data={
                  //   activeTab === "all"
                  //     ? tabData?.data || []
                  //     : [
                  //         {
                  //           page_docs: tabData?.data,
                  //           panels: tabData?.panels,
                  //           page: tabData?.page,
                  //         },
                  //       ]
                  // }
                  data={simpleDetailData}
                  loading={searchLoading}
                  refetchData={refreshPhotoData}
                  refetchLoading={searchLoading}
                  pageView={currentPage}
                  tabData={
                    activeTab === "all"
                      ? tabData?.data || []
                      : [
                          {
                            page_docs: tabData?.data,
                            panels: tabData?.panels,
                            page: tabData?.page,
                          },
                        ]
                  }
                />
              </>
            ) : currentPage === "summary" ? (
              <>
                <NewPhotoSummaryTab
                  doc={simpleDetailData}
                  containerHeight={containerHeight}
                  refetchData={refreshPhotoData}
                />
              </>
            ) : (
              <>
                <NewPhotoShownOnPage
                  data2={simpleDetailData}
                  data={
                    rowsTabData
                    // activeTab === "all"
                    //   ? tabData?.data || []
                    //   : [
                    //       {
                    //         page_docs: tabData?.data,
                    //         panels: tabData?.panels,
                    //         page: tabData?.page,
                    //         page_slots: tabData?.page_slots,
                    //       },
                    //     ]
                  }
                  loading={searchLoading}
                  refetchData={refreshPhotoData}
                  refetchLoading={searchLoading}
                  pageView={currentPage}
                  // tabData={
                  //   activeTab === "all"
                  //     ? tabData?.data || []
                  //     : [
                  //         {
                  //           page_docs: tabData?.data,
                  //           panels: tabData?.panels,
                  //           page: tabData?.page,
                  //           page_slots: tabData?.page_slots,
                  //         },
                  //       ]
                  // }
                  activeTab={activeTab}
                />
              </>
            )}

            <NotesSectionDashboard pageName={"Photos"} />
          </div>
        </div>
      </div>

      <Footer />
    </>
  );
};

export default NewPhotoPage;
